'use client';

import { Button, ButtonGroup, Dialog, Text } from '@/_components';
import { useCallback, useContext, useState, type FC } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { restoreDeletedDocument } from '@/_api/admin-documents';
import type { RestoreDocumentDialogProps } from './RestoreDocumentDialog.types';

export const RestoreDocumentDialog: FC<RestoreDocumentDialogProps> = ({
  isOpen,
  onClose,
  selectedDocuments,
}) => {
  const { token } = useContext(UserContext);
  const { reloadDocuments, openDirectory, companyId } =
    useContext(DocumentsContext);
  const { addToast } = useContext(ToastContext);
  const [isRestoring, setIsRestoring] = useState(false);

  const onRestore = useCallback(async () => {
    if (!token || !companyId) {
      return;
    }

    try {
      setIsRestoring(true);
      await Promise.all(
        selectedDocuments.map((document) =>
          restoreDeletedDocument({
            companyId,
            extractedDocumentId: document.extracted_document_id!,
            token,
          }),
        ),
      );
      addToast('success', 'Documents restored successfully');
    } catch (error) {
      addToast('error', 'Failed to restore documents', { error });
    } finally {
      reloadDocuments(openDirectory);
      setIsRestoring(false);
      onClose();
    }
  }, [
    selectedDocuments,
    token,
    reloadDocuments,
    openDirectory,
    onClose,
    addToast,
    companyId,
  ]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Restore Documents">
      <Dialog.Content>
        {selectedDocuments.length === 1 ? (
          <Text>Are you sure you want to restore this document?</Text>
        ) : (
          <Text>
            Are you sure you want to restore {selectedDocuments.length} selected
            documents?
          </Text>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="ghost"
            value="Cancel"
            onClick={onClose}
            isDisabled={isRestoring}
          />
          <Button
            intent="primary"
            value="Restore"
            onClick={onRestore}
            isLoading={isRestoring}
            isDisabled={isRestoring}
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
